import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { ViewConfig } from "@lib/views";
import { DesignTokenTab } from "./designTokensCatalog";
import {
    DesignTokenNameCell,
    DesignTokenOriginCell,
    DesignTokenSampleCell,
    DesignTokenSourceCell,
    DesignTokenValueCell,
} from "./designTokensViewCells";
import { DesignTokenUsageCell } from "./designTokensMotionCells";
import { TokenViewRow } from "./designTokensViewRows";

const TOKEN_COLUMN_ID = "token";
const ORIGIN_COLUMN_ID = "origin";
const SOURCE_COLUMN_ID = "source";
const VALUE_COLUMN_ID = "value";
const USAGE_COLUMN_ID = "usage";
const SAMPLE_COLUMN_ID = "sample";

const TOKEN_MIN_SIZE = 260;
const TOKEN_MAX_SIZE = 420;
const ORIGIN_MIN_SIZE = 100;
const ORIGIN_MAX_SIZE = 140;
const SOURCE_MIN_SIZE = 220;
const SOURCE_MAX_SIZE = 380;
const VALUE_MIN_SIZE = 260;
const VALUE_MAX_SIZE = 420;
const USAGE_MIN_SIZE = 280;
const USAGE_MAX_SIZE = 420;
const SAMPLE_MIN_SIZE = 220;

// The typography catalog is the only one whose four columns are all populated. Its
// reservations fit every column on screen from 1024px up, and below that the table
// scrolls sideways rather than hiding any of a mixin's recipe or its live sample.
const MIXIN_TOKEN_MIN_SIZE = 160;
const MIXIN_TOKEN_MAX_SIZE = 240;
const MIXIN_SOURCE_MIN_SIZE = 260;
const MIXIN_SOURCE_MAX_SIZE = 360;
const MIXIN_VALUE_MIN_SIZE = 160;
const MIXIN_VALUE_MAX_SIZE = 240;
const MIXIN_SAMPLE_MIN_SIZE = 320;
const MIXIN_SAMPLE_MAX_SIZE = 360;

const isTypographyTab = (tab: DesignTokenTab) => tab.id === "typography";

/** Only these two catalogs compose their rows from tokens on another page. */
const hasSourceColumn = (tab: DesignTokenTab) =>
    tab.id === "semantic-colors" || isTypographyTab(tab);

/** Motion tokens are picked by role, so their catalog says what each is for. */
const hasUsageColumn = (tab: DesignTokenTab) => tab.id === "motion";

/** Ephemeral view — the playground never persists column layout. */
export const buildDesignTokensView = (tab: DesignTokenTab): View => ({
    type: ViewType.DESIGN_TOKENS,
    columns: [
        { id: TOKEN_COLUMN_ID },
        { id: ORIGIN_COLUMN_ID },
        ...(hasSourceColumn(tab) ? [{ id: SOURCE_COLUMN_ID }] : []),
        { id: VALUE_COLUMN_ID },
        ...(hasUsageColumn(tab) ? [{ id: USAGE_COLUMN_ID }] : []),
        { id: SAMPLE_COLUMN_ID },
    ],
    filters: {},
    options: {
        selection: false,
        bulkActions: false,
        density: ViewDensity.SM,
    },
});

export const buildDesignTokensConfig = (
    tab: DesignTokenTab,
): ViewConfig<TokenViewRow, string, never> => ({
    columns: {
        [TOKEN_COLUMN_ID]: {
            accessorKey: "label",
            name: isTypographyTab(tab) ? "Mixin" : "Token",
            header: isTypographyTab(tab) ? "Mixin" : "Token",
            cell: DesignTokenNameCell,
            minSize: isTypographyTab(tab)
                ? MIXIN_TOKEN_MIN_SIZE
                : TOKEN_MIN_SIZE,
            maxSize: isTypographyTab(tab)
                ? MIXIN_TOKEN_MAX_SIZE
                : TOKEN_MAX_SIZE,
            immutable: true,
        },
        [ORIGIN_COLUMN_ID]: {
            accessorKey: "label",
            name: "Origin",
            header: "Origin",
            cell: DesignTokenOriginCell,
            minSize: ORIGIN_MIN_SIZE,
            maxSize: ORIGIN_MAX_SIZE,
        },
        [SOURCE_COLUMN_ID]: {
            accessorKey: "label",
            name: "Source",
            header: "Source",
            cell: DesignTokenSourceCell,
            minSize: isTypographyTab(tab)
                ? MIXIN_SOURCE_MIN_SIZE
                : SOURCE_MIN_SIZE,
            maxSize: isTypographyTab(tab)
                ? MIXIN_SOURCE_MAX_SIZE
                : SOURCE_MAX_SIZE,
        },
        [VALUE_COLUMN_ID]: {
            accessorKey: "label",
            name: "Value",
            header: "Value",
            cell: DesignTokenValueCell,
            minSize: isTypographyTab(tab)
                ? MIXIN_VALUE_MIN_SIZE
                : VALUE_MIN_SIZE,
            maxSize: isTypographyTab(tab)
                ? MIXIN_VALUE_MAX_SIZE
                : VALUE_MAX_SIZE,
        },
        [USAGE_COLUMN_ID]: {
            accessorKey: "label",
            name: "Use",
            header: "Use",
            cell: DesignTokenUsageCell,
            minSize: USAGE_MIN_SIZE,
            maxSize: USAGE_MAX_SIZE,
        },
        [SAMPLE_COLUMN_ID]: {
            accessorKey: "label",
            name: "Sample",
            header: "Sample",
            cell: DesignTokenSampleCell,
            minSize: isTypographyTab(tab)
                ? MIXIN_SAMPLE_MIN_SIZE
                : SAMPLE_MIN_SIZE,
            maxSize: isTypographyTab(tab) ? MIXIN_SAMPLE_MAX_SIZE : undefined,
        },
    },
    nesting: {
        enabled: true,
        expandColumn: TOKEN_COLUMN_ID,
        childrenKey: "children",
    },
});
